import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ChangePasswordPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) {
    navigate("/login");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPwd || !newPwd) {
      toast.error("يرجى تعبئة جميع الحقول");
      return;
    }
    if (newPwd.length < 6) {
      toast.error("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("تأكيد كلمة المرور غير مطابق");
      return;
    }
    if (newPwd === currentPwd) {
      toast.error("كلمة المرور الجديدة يجب أن تختلف عن الحالية");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      await refreshUser();
      toast.success("تم تغيير كلمة المرور بنجاح");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل تغيير كلمة المرور");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F4EF] p-6" dir="rtl">
      <div className="w-full max-w-md bg-white border border-[#1A1C19]/10 rounded-lg shadow-3d-sm p-8 fade-up" data-testid="change-password-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-md bg-[#1F3A33] text-white flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-readex font-semibold text-xl text-[#1A1C19]">تغيير كلمة المرور</h2>
            <div className="text-xs text-[#8A8D86]">{user.email}</div>
          </div>
        </div>

        {user.must_change_password && (
          <div className="mb-5 bg-[#FBE9E3] border border-[#C84C31]/30 text-[#7A2516] rounded-md px-4 py-3 text-sm font-readex" data-testid="forced-change-banner">
            يجب تغيير كلمة المرور المؤقتة قبل استخدام التطبيق.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
          <PasswordField
            label="كلمة المرور الحالية"
            value={currentPwd}
            onChange={setCurrentPwd}
            show={show}
            toggle={() => setShow((s) => !s)}
            testid="current-password-input"
          />
          <PasswordField
            label="كلمة المرور الجديدة"
            value={newPwd}
            onChange={setNewPwd}
            show={show}
            toggle={() => setShow((s) => !s)}
            testid="new-password-input"
          />
          <PasswordField
            label="تأكيد كلمة المرور الجديدة"
            value={confirmPwd}
            onChange={setConfirmPwd}
            show={show}
            toggle={() => setShow((s) => !s)}
            testid="confirm-password-input"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="w-full bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 transition-colors rounded-md px-6 py-3 font-medium font-readex"
          >
            {loading ? "جاري التغيير…" : "حفظ كلمة المرور الجديدة"}
          </button>

          {!user.must_change_password && (
            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full text-sm text-[#5C605A] hover:text-[#1A1C19] font-readex"
              data-testid="change-password-cancel"
            >
              إلغاء والعودة
            </button>
          )}
          {user.must_change_password && (
            <button
              type="button"
              onClick={() => { logout(); navigate("/login"); }}
              className="w-full text-sm text-[#8A8D86] hover:text-[#C84C31] font-readex"
              data-testid="change-password-logout"
            >
              تسجيل الخروج
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, toggle, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">{label}</label>
      <div className="relative">
        <Lock className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-[#8A8D86]" />
        <input
          type={show ? "text" : "password"}
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-[#1A1C19]/15 rounded-md ps-10 pe-12 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1F3A33]/15 focus:border-[#1F3A33] transition-all"
          dir="ltr"
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute end-3 top-1/2 -translate-y-1/2 text-[#8A8D86] hover:text-[#1A1C19]"
        >
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
